import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Block as VoidIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { PageHeader, Loading, EmptyState } from '../components/Layout';
import { toast } from 'react-toastify';
import paymentService from '../services/paymentService';
import tenantService from '../services/tenantService';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    tenant_id: '',
    payment_method: '',
    start_date: '',
    end_date: '',
  });
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [unmatchedPayments, setUnmatchedPayments] = useState([]);
  const [applyingUnmatched, setApplyingUnmatched] = useState(null);

  useEffect(() => {
    fetchPayments();
    fetchTenants();
    fetchUnmatchedPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      console.log('Fetching all payments...');
      const response = await paymentService.getAllPayments();
      console.log('Payments received:', response.data);
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading payments:', error);
      console.error('Error response:', error.response);
      setPayments([]);
      if (error.response?.status !== 404) {
        toast.error(error.response?.data?.detail || 'Failed to load payments');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const fetchUnmatchedPayments = async () => {
    try {
      const response = await paymentService.getUnmatchedPayments();
      setUnmatchedPayments(response.data?.filter(u => u.status === 'pending') || []);
    } catch (error) {
      console.log('No unmatched payments or error:', error);
    }
  };

  const handleApplyUnmatched = async (unmatchedId, invoiceId) => {
    try {
      setApplyingUnmatched(unmatchedId);
      const response = await paymentService.applyUnmatchedPayment(unmatchedId, invoiceId);
      toast.success(`Payment applied to ${response.data.invoice_number}`);
      fetchUnmatchedPayments();
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply payment');
    } finally {
      setApplyingUnmatched(null);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenVoidDialog = (payment) => {
    setSelectedPayment(payment);
    setVoidReason('');
    setVoidDialog(true);
  };

  const handleVoidPayment = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason for voiding');
      return;
    }
    try {
      const response = await paymentService.voidPayment(selectedPayment.id, voidReason);
      const data = response.data;
      toast.success(
        `Payment #${selectedPayment.id} voided. KES ${selectedPayment.amount?.toLocaleString()} reversed. Invoice status: ${data.invoice_status?.toUpperCase()}.`,
        { autoClose: 8000 }
      );
      setVoidDialog(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void payment');
    }
  };

  const getPaymentMethodColor = (method) => {
    switch (method?.toLowerCase()) {
      case 'mpesa':
      case 'm-pesa':
        return 'success';
      case 'bank':
      case 'bank_transfer':
        return 'primary';
      case 'cash':
        return 'default';
      case 'card':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleDownloadReceipt = (payment) => {
    // Check if payment has credit generated from overpayment
    const hasCreditGenerated = payment.credit_added && payment.credit_added > 0;
    
    // Create receipt HTML matching the screenshot design
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Payment Receipt - ${payment.invoice_number}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
            background: white;
          }
          .receipt-header {
            text-align: center;
            border-bottom: 3px solid #2196F3;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .receipt-header h1 {
            color: #2196F3;
            margin: 0 0 8px 0;
            font-size: 36px;
            font-weight: 700;
            letter-spacing: 1px;
          }
          .receipt-header p {
            margin: 0 0 12px 0;
            color: #666;
            font-size: 16px;
          }
          .status-badge {
            display: inline-block;
            padding: 8px 24px;
            background: #4CAF50;
            color: white;
            border-radius: 20px;
            font-weight: 600;
            font-size: 14px;
            letter-spacing: 0.5px;
          }
          .receipt-info {
            background: #f5f5f5;
            padding: 0;
            border-radius: 4px;
            margin-bottom: 30px;
            overflow: hidden;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 24px;
            border-bottom: 1px solid #e0e0e0;
            background: white;
            margin-bottom: 1px;
          }
          .info-row:last-child {
            margin-bottom: 0;
          }
          .info-label {
            font-weight: 600;
            color: #555;
            font-size: 15px;
          }
          .info-value {
            color: #333;
            font-size: 15px;
            text-align: right;
          }
          .amount-section {
            background: #E3F2FD;
            padding: 30px;
            border-radius: 4px;
            text-align: center;
            margin: 30px 0;
          }
          .amount-label {
            font-size: 16px;
            color: #666;
            margin-bottom: 12px;
            font-weight: 500;
          }
          .amount-value {
            font-size: 48px;
            font-weight: 700;
            color: #2196F3;
            letter-spacing: -1px;
          }
          .credit-notice {
            background: #F1F8E9;
            border-left: 4px solid #4CAF50;
            padding: 20px 24px;
            margin: 30px 0;
            border-radius: 4px;
          }
          .credit-notice-title {
            color: #2E7D32;
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
          }
          .credit-notice-title::before {
            content: '✓';
            margin-right: 8px;
            font-weight: bold;
          }
          .credit-notice-text {
            color: #33691E;
            font-size: 14px;
            line-height: 1.6;
          }
          .credit-amount {
            font-weight: 700;
          }
          .receipt-footer {
            text-align: center;
            margin-top: 50px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            color: #999;
            font-size: 12px;
            line-height: 1.8;
          }
          @media print {
            body { 
              padding: 20px;
            }
            .no-print {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt-header">
          <h1>PAYMENT RECEIPT</h1>
          <p>Property Management System</p>
          <div class="status-badge">PAID</div>
        </div>
        
        <div class="receipt-info">
          <div class="info-row">
            <span class="info-label">Receipt Date:</span>
            <span class="info-value">${new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span class="info-value">${new Date(payment.payment_date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Invoice Number:</span>
            <span class="info-value">#${payment.invoice_number || payment.invoice_id}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Method:</span>
            <span class="info-value">${payment.payment_method?.toUpperCase()}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Transaction Reference:</span>
            <span class="info-value">${payment.transaction_ref || 'N/A'}</span>
          </div>
        </div>
        
        <div class="amount-section">
          <div class="amount-label">Amount Paid</div>
          <div class="amount-value">KES ${payment.amount?.toLocaleString()}</div>
        </div>
        
        ${hasCreditGenerated ? `
        <div class="credit-notice">
          <div class="credit-notice-title">Credit Generated from Overpayment</div>
          <div class="credit-notice-text">
            An overpayment of <span class="credit-amount">KES ${payment.credit_added?.toLocaleString()}</span> has been credited to your account for future use.
          </div>
        </div>
        ` : ''}
        
        <div class="receipt-footer">
          <p>This is a computer-generated receipt and is valid without signature.</p>
          <p>Thank you for your payment!</p>
          <p>Generated on ${new Date().toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>
      </body>
      </html>
    `;

    // Create a blob and download
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${payment.invoice_number || payment.id}-${new Date().getTime()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Receipt downloaded successfully!');
  };

  // Apply filters
  const filteredPayments = payments.filter((payment) => {
    if (filters.tenant_id && payment.tenant_id !== parseInt(filters.tenant_id)) {
      return false;
    }
    if (filters.payment_method && payment.payment_method !== filters.payment_method) {
      return false;
    }
    if (filters.start_date && new Date(payment.payment_date) < new Date(filters.start_date)) {
      return false;
    }
    if (filters.end_date && new Date(payment.payment_date) > new Date(filters.end_date)) {
      return false;
    }
    return true;
  });

  // Calculate statistics
  const totalPayments = filteredPayments.length;
  const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const cashPayments = filteredPayments.filter(p => p.payment_method === 'cash').length;
  const mpesaPayments = filteredPayments.filter(p => ['mpesa', 'm-pesa'].includes(p.payment_method?.toLowerCase())).length;
  const bankPayments = filteredPayments.filter(p => ['bank', 'bank_transfer'].includes(p.payment_method?.toLowerCase())).length;

  if (loading) {
    return <Loading message="Loading payments..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Payments"
        subtitle="View and manage all payment transactions"
        breadcrumbs={[{ label: 'Payments' }]}
        action={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPayments}
          >
            Refresh
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total Payments
                  </Typography>
                  <Typography variant="h4">{totalPayments}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    KES {totalAmount.toLocaleString()}
                  </Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Cash
                  </Typography>
                  <Typography variant="h4">{cashPayments}</Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 48, color: 'grey.500' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    M-Pesa
                  </Typography>
                  <Typography variant="h4">{mpesaPayments}</Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Bank Transfer
                  </Typography>
                  <Typography variant="h4">{bankPayments}</Typography>
                </Box>
                <PaymentIcon sx={{ fontSize: 48, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unmatched Payments Alert */}
      {unmatchedPayments.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, border: '2px solid #ff9800', bgcolor: '#fff3e0' }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <WarningIcon color="warning" />
            <Typography variant="h6" color="warning.dark" fontWeight="600">
              {unmatchedPayments.length} Unmatched M-Pesa Payment{unmatchedPayments.length > 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              Tenant entered wrong invoice number. Select the correct invoice to apply.
            </Typography>
          </Box>
          {unmatchedPayments.map((u) => (
            <Paper key={u.id} sx={{ p: 2, mb: 1, bgcolor: 'white' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography variant="body2" fontWeight="600">
                    {u.payer_name || 'Unknown'} — KES {u.amount?.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Trans: {u.trans_id} | Phone: {u.phone_number} | Entered: {u.bill_ref_number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Received: {u.received_at ? new Date(u.received_at).toLocaleString() : 'N/A'}
                  </Typography>
                  {u.suggested_tenant && (
                    <Chip label={`Matched tenant: ${u.suggested_tenant.full_name}`} color="info" size="small" sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                  )}
                </Grid>
                <Grid item xs={12} md={7}>
                  {u.suggested_invoices && u.suggested_invoices.length > 0 ? (
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {u.suggested_invoices.map((inv) => (
                        <Button
                          key={inv.id}
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckIcon />}
                          disabled={applyingUnmatched === u.id}
                          onClick={() => handleApplyUnmatched(u.id, inv.id)}
                        >
                          {inv.invoice_number} (KES {inv.remaining?.toLocaleString()})
                        </Button>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No matching tenant found by phone. Apply manually from invoice list.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Filter by Tenant"
              name="tenant_id"
              value={filters.tenant_id}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.full_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Payment Method"
              name="payment_method"
              value={filters.payment_method}
              onChange={handleFilterChange}
              size="small"
            >
              <MenuItem value="">All Methods</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="mpesa">M-Pesa</MenuItem>
              <MenuItem value="card">Card</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="From Date"
              name="start_date"
              type="date"
              value={filters.start_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="To Date"
              name="end_date"
              type="date"
              value={filters.end_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Payments Table */}
      {filteredPayments.length === 0 ? (
        <EmptyState
          icon={PaymentIcon}
          title="No Payment History"
          description={payments.length === 0 
            ? "No payments have been recorded yet. Payments will appear here once tenants make payments or you manually record them."
            : "No payments match your filter criteria. Try adjusting your filters."
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Tenant</strong></TableCell>
                <TableCell><strong>Invoice #</strong></TableCell>
                <TableCell><strong>Amount (KES)</strong></TableCell>
                <TableCell><strong>Method</strong></TableCell>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell><strong>Notes</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    {new Date(payment.payment_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{payment.tenant_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      #{payment.invoice_number || payment.invoice_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {payment.amount?.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.payment_method?.toUpperCase()}
                      color={getPaymentMethodColor(payment.payment_method)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {payment.transaction_ref || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ maxWidth: 200, display: 'block' }}>
                      {payment.notes || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download Receipt">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDownloadReceipt(payment)}
                      >
                        <ReceiptIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Void Payment">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenVoidDialog(payment)}
                      >
                        <VoidIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Void Payment Dialog */}
      <Dialog open={voidDialog} onClose={() => setVoidDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Void Payment</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                You are about to void a payment of <strong>KES {selectedPayment.amount?.toLocaleString()}</strong> for
                invoice <strong>#{selectedPayment.invoice_number}</strong> ({selectedPayment.tenant_name}).
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Method: {selectedPayment.payment_method?.toUpperCase()} | Ref: {selectedPayment.transaction_ref || 'N/A'}
              </Typography>
              <Typography variant="body2" color="error" gutterBottom>
                This will reverse the payment amount, any commission, and recalculate the invoice status.
              </Typography>
              <TextField
                fullWidth
                label="Reason for voiding"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                required
                placeholder="e.g. Duplicate payment, entered wrong amount"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialog(false)}>Cancel</Button>
          <Button onClick={handleVoidPayment} variant="contained" color="error">
            Void Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
